import { useEffect, useState } from "react";
import DashboardLayout from "@/components/DashboardLayout";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { UserCog, ShieldAlert, Globe2, Link2, Clock, Copy, Trash2, Plus, LogOut } from "lucide-react";
import { toast } from "sonner";
import AdminPromoCodes from "@/components/AdminPromoCodes";
import AdminFunnelDashboard from "@/components/AdminFunnelDashboard";
import AdminLeads from "@/components/AdminLeads";
import AdminDangerZone from "@/components/AdminDangerZone";
import AdminClientLogs from "@/components/AdminClientLogs";
import AdminMarketingLeads from "@/components/AdminMarketingLeads";
import AdminInvitations from "@/components/AdminInvitations";
import AdminWorkspaceSettings from "@/components/AdminWorkspaceSettings";

export default function Admin() {
  const { user: currentUser, refresh } = useAuth();
  const [users, setUsers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [editing, setEditing] = useState(null);
  const [selected, setSelected] = useState([]);

  // Inspection tokens
  const [tokens, setTokens] = useState([]);
  const [tokenDialogOpen, setTokenDialogOpen] = useState(false);
  const [tokenForm, setTokenForm] = useState({
    label: "", inspector_name: "", inspector_organization: "",
    expires_in_hours: 72,
    scope_types: ["capa", "deviation", "change", "document", "training_assignment", "codesign_session", "risk", "device"],
  });
  const [freshToken, setFreshToken] = useState(null);

  const load = async () => {
    try {
      const [u, r, t] = await Promise.all([
        api.get("/admin/users"),
        api.get("/admin/roles"),
        api.get("/inspection/tokens"),
      ]);
      setUsers(u.data);
      setRoles(r.data.roles);
      setTokens(t.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Admin access required");
    }
  };
  useEffect(() => { load(); }, []);

  const createToken = async () => {
    if (!tokenForm.label) return toast.error("Label required");
    try {
      const { data } = await api.post("/inspection/tokens", tokenForm);
      setFreshToken(data);
      setTokenDialogOpen(false);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to create");
    }
  };

  const revokeToken = async (tokenId) => {
    if (!window.confirm("Revoke this inspection link? Auditors using it will lose access immediately.")) return;
    await api.post(`/inspection/tokens/${tokenId}/revoke`);
    toast.success("Token revoked");
    load();
  };

  const copyLink = (token) => {
    const url = `${window.location.origin}/inspect/${token}`;
    navigator.clipboard.writeText(url);
    toast.success("Link copied to clipboard");
  };

  const toggleScope = (t) => {
    setTokenForm({
      ...tokenForm,
      scope_types: tokenForm.scope_types.includes(t)
        ? tokenForm.scope_types.filter((x) => x !== t)
        : [...tokenForm.scope_types, t],
    });
  };

  const startEdit = (u) => {
    setEditing(u);
    setSelected(u.roles || []);
  };

  const save = async () => {
    const editedSelf = editing?.user_id === currentUser?.user_id;
    await api.patch(`/admin/users/${editing.user_id}/roles`, { roles: selected });
    if (editedSelf) {
      // Immediately re-fetch /auth/me so the sidebar & any UI reading user.roles
      // reflects the new roles without a page reload.
      await refresh();
      toast.success("Your roles have been updated — session refreshed");
    } else {
      toast.success(
        `Roles updated for ${editing.email}. Ask them to click "Reload my session" (or log out and back in) for the change to take effect in their browser.`,
        { duration: 8000 },
      );
    }
    setEditing(null);
    load();
  };

  const toggle = (r) => setSelected(selected.includes(r) ? selected.filter((x) => x !== r) : [...selected, r]);

  const revokeSessions = async (u) => {
    if (!window.confirm(`Force sign-out for ${u.email} on all devices? They'll need to log in again.`)) return;
    try {
      await api.post(`/admin/users/${u.user_id}/revoke-sessions`);
      toast.success(`${u.email} signed out from all devices`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to revoke sessions");
    }
  };

  return (
    <DashboardLayout>
      <div className="p-6 lg:p-10 max-w-6xl">
        <div className="vq-overline mb-2">Administration</div>
        <h1 className="font-display font-extrabold text-3xl sm:text-4xl tracking-tight text-slate-900 mb-2">Users & Roles (RBAC)</h1>
        <p className="text-slate-600 mb-4">Assign roles to control access to modules. Admin bypasses all checks.</p>

        {/* Signed-in identity banner — surfaces the actual roles the backend
             sees so users can immediately diagnose "why can't I create a
             link / send an invite" (the answer is almost always: this
             account doesn't have Admin in its roles array). */}
        {currentUser && (
          <div
            data-testid="admin-identity-banner"
            className={`mb-8 rounded-lg border px-4 py-3 ${
              (currentUser.roles || []).includes("Admin")
                ? "bg-slate-50 border-slate-200"
                : "bg-amber-50 border-amber-300"
            }`}
          >
            <div className="flex flex-wrap items-center gap-x-6 gap-y-2 text-sm">
              <div>
                <span className="font-mono text-[10px] uppercase tracking-wider text-slate-500">Signed in as</span>
                <div className="font-display font-bold text-slate-900" data-testid="admin-identity-email">{currentUser.email}</div>
              </div>
              <div className="flex-1 min-w-[280px]">
                <span className="font-mono text-[10px] uppercase tracking-wider text-slate-500">Your roles ({(currentUser.roles || []).length})</span>
                <div className="flex flex-wrap gap-1 mt-1" data-testid="admin-identity-roles">
                  {(currentUser.roles || []).length === 0
                    ? <span className="text-[11px] font-mono text-red-700">no roles assigned</span>
                    : (currentUser.roles || []).map((r) => (
                      <span key={r} className={`px-1.5 py-0.5 text-[10px] font-mono border rounded ${
                        r === "Admin"
                          ? "border-emerald-300 bg-emerald-50 text-emerald-700"
                          : "border-slate-200 bg-white text-slate-700"
                      }`}>{r}</span>
                    ))}
                </div>
              </div>
            </div>
            {!(currentUser.roles || []).includes("Admin") && (
              <div className="mt-3 pt-3 border-t border-amber-300 text-xs text-amber-900" data-testid="admin-identity-warning">
                <strong>Heads-up:</strong> Admin-only actions on this page (creating invite links, editing roles, revoking sessions) will fail with a <span className="font-mono">Requires role(s): Admin</span> error until a workspace admin grants you the <span className="font-mono">Admin</span> role. To fix: ask an existing admin to open this page, find your row in the users table below, and add the <span className="font-mono">Admin</span> role.
              </div>
            )}
          </div>
        )}

        {/* Conversion & revenue */}
        <div className="mb-10">
          <AdminFunnelDashboard />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
          <div className="vq-card p-5"><ShieldAlert className="w-5 h-5 text-[#0055FF] mb-2" />
            <div className="vq-overline mb-1">21 CFR Part 11 § 11.10(d)</div>
            <div className="text-sm text-slate-700">System access limited to authorized individuals via role-based permissions.</div>
          </div>
          <div className="vq-card p-5"><UserCog className="w-5 h-5 text-[#0055FF] mb-2" />
            <div className="vq-overline mb-1">Roles available</div>
            <div className="text-sm text-slate-700">{roles.length ? roles.join(" • ") : "Admin, QA Lead, RA, Clinical, Manufacturing, Auditor, Patient Advisor"}</div>
          </div>
          <div className="vq-card p-5"><Globe2 className="w-5 h-5 text-[#0055FF] mb-2" />
            <div className="vq-overline mb-1">Data residency</div>
            <div className="text-sm text-slate-700">Enterprise plan supports EU (Frankfurt) and India (Mumbai) MongoDB residency. Current: US-East.</div>
          </div>
        </div>

        <div className="vq-card overflow-hidden">
          <table className="w-full">
            <thead>
              <tr className="border-b border-slate-200 bg-slate-50">
                <th className="text-left px-6 py-3 text-xs font-mono uppercase tracking-wider text-slate-500">User</th>
                <th className="text-left px-6 py-3 text-xs font-mono uppercase tracking-wider text-slate-500">Plan</th>
                <th className="text-left px-6 py-3 text-xs font-mono uppercase tracking-wider text-slate-500">Roles</th>
                <th className="text-left px-6 py-3 text-xs font-mono uppercase tracking-wider text-slate-500">Auth</th>
                <th className="text-right px-6 py-3 text-xs font-mono uppercase tracking-wider text-slate-500">Actions</th>
              </tr>
            </thead>
            <tbody>
              {users.map((u) => (
                <tr key={u.user_id} className="border-b border-slate-100">
                  <td className="px-6 py-4">
                    <div className="font-display font-bold text-slate-900">{u.name}</div>
                    <div className="text-xs font-mono text-slate-500">{u.email}</div>
                  </td>
                  <td className="px-6 py-4 text-sm capitalize">{u.plan}</td>
                  <td className="px-6 py-4">
                    <div className="flex flex-wrap gap-1">
                      {(u.roles || []).map((r) => (
                        <span key={r} className="px-1.5 py-0.5 text-[10px] font-mono border border-slate-200 rounded text-slate-700 bg-slate-50">{r}</span>
                      ))}
                    </div>
                  </td>
                  <td className="px-6 py-4 text-xs font-mono text-slate-500">{u.auth_provider || "jwt"}</td>
                  <td className="px-6 py-4 text-right">
                    <div className="flex justify-end gap-2">
                      {(u.roles || []).includes("Admin") && (u.roles || []).length < roles.length && (
                        <Button size="sm" variant="outline"
                                onClick={async () => {
                                  try {
                                    await api.post(`/admin/users/${u.user_id}/grant-all`);
                                    if (u.user_id === currentUser?.user_id) {
                                      await refresh();
                                      toast.success("All workspace roles granted — session refreshed");
                                    } else {
                                      toast.success(
                                        `All roles granted to ${u.email}. Ask them to click "Reload my session" for the change to take effect.`,
                                        { duration: 8000 },
                                      );
                                    }
                                    load();
                                  } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
                                }}
                                data-testid={`admin-grant-all-${u.user_id}`}
                                className="border-emerald-300 text-emerald-700 hover:bg-emerald-50 font-semibold">
                          Grant all roles
                        </Button>
                      )}
                      <Button size="sm" variant="outline" onClick={() => startEdit(u)} data-testid={`admin-edit-${u.user_id}`} className="border-slate-300 font-semibold">
                        Edit roles
                      </Button>
                      {u.user_id !== currentUser?.user_id && (
                        <Button size="sm" variant="ghost" onClick={() => revokeSessions(u)}
                                data-testid={`admin-revoke-sessions-${u.user_id}`}
                                title="Sign this user out of all devices"
                                className="text-slate-500 hover:text-red-600 hover:bg-red-50">
                          <LogOut className="w-3.5 h-3.5" />
                        </Button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
              {users.length === 0 && <tr><td colSpan={5} className="text-center py-12 text-slate-500">Only Admins can view this page.</td></tr>}
            </tbody>
          </table>
        </div>

        {/* Invitations — extracted into AdminInvitations component so this
             file only orchestrates the admin page rather than owning every
             detail of every workflow. */}
        <AdminInvitations roles={roles} />

        {/* Inspection Mode */}
        <div className="mt-12">
          <div className="flex items-end justify-between flex-wrap gap-4 mb-6">
            <div>
              <div className="vq-overline mb-2">Inspection Mode</div>
              <h2 className="font-display font-bold text-2xl text-slate-900">Auditor Access Links</h2>
              <p className="text-sm text-slate-600 mt-1 max-w-2xl">
                Mint a time-boxed, read-only link for an FDA / Notified Body / CDSCO inspector. Every access is logged, and revocation is instant. Perfect for inspection readiness.
              </p>
            </div>
            <Button onClick={() => setTokenDialogOpen(true)} data-testid="admin-insp-new-btn"
                    className="h-11 bg-slate-900 hover:bg-[#0055FF] text-white font-semibold">
              <Plus className="w-4 h-4 mr-2" /> New inspection link
            </Button>
          </div>

          <div className="vq-card overflow-hidden">
            <table className="w-full">
              <thead>
                <tr className="border-b border-slate-200 bg-slate-50">
                  <th className="text-left px-6 py-3 text-xs font-mono uppercase tracking-wider text-slate-500">Label</th>
                  <th className="text-left px-6 py-3 text-xs font-mono uppercase tracking-wider text-slate-500">Inspector</th>
                  <th className="text-left px-6 py-3 text-xs font-mono uppercase tracking-wider text-slate-500">Expires</th>
                  <th className="text-left px-6 py-3 text-xs font-mono uppercase tracking-wider text-slate-500">Access</th>
                  <th className="text-right px-6 py-3 text-xs font-mono uppercase tracking-wider text-slate-500">Actions</th>
                </tr>
              </thead>
              <tbody>
                {tokens.map((t) => {
                  const expired = new Date(t.expires_at) < new Date();
                  return (
                    <tr key={t.token_id} className={`border-b border-slate-100 ${t.revoked || expired ? "opacity-50" : ""}`} data-testid={`admin-insp-row-${t.token_id}`}>
                      <td className="px-6 py-4">
                        <div className="font-display font-bold text-sm text-slate-900">{t.label}</div>
                        <div className="text-[10px] font-mono text-slate-500">by {t.created_by}</div>
                      </td>
                      <td className="px-6 py-4 text-sm">
                        {t.inspector_name || "—"}
                        <div className="text-[10px] font-mono text-slate-500">{t.inspector_organization}</div>
                      </td>
                      <td className="px-6 py-4 text-xs font-mono text-slate-600">
                        <Clock className="w-3 h-3 inline mr-1" />
                        {new Date(t.expires_at).toLocaleString()}
                        {t.revoked && <span className="ml-2 text-red-600">REVOKED</span>}
                        {!t.revoked && expired && <span className="ml-2 text-slate-500">EXPIRED</span>}
                      </td>
                      <td className="px-6 py-4 text-xs font-mono text-slate-600">
                        {(t.access_log || []).length} view(s)
                      </td>
                      <td className="px-6 py-4 text-right">
                        <div className="inline-flex gap-1">
                          {!t.revoked && !expired && (
                            <Button size="sm" variant="outline" onClick={() => copyLink(t.token)}
                                    data-testid={`admin-insp-copy-${t.token_id}`}
                                    className="border-slate-300 font-semibold">
                              <Copy className="w-3.5 h-3.5 mr-1" /> Copy link
                            </Button>
                          )}
                          {!t.revoked && (
                            <Button size="sm" variant="ghost" onClick={() => revokeToken(t.token_id)}
                                    data-testid={`admin-insp-revoke-${t.token_id}`}>
                              <Trash2 className="w-3.5 h-3.5 text-red-500" />
                            </Button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
                {tokens.length === 0 && (
                  <tr><td colSpan={5} className="text-center py-12 text-slate-500">
                    <Link2 className="w-8 h-8 text-slate-300 mx-auto mb-3" />
                    No inspection links yet. Create one for your next audit.
                  </td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Promo codes */}
        <div className="mt-8">
          <AdminPromoCodes />
        </div>

        {/* Sales leads */}
        <div className="mt-8">
          <AdminLeads />
        </div>

        {/* Early-access / marketing lead pipeline */}
        <AdminMarketingLeads />

        {/* Workspace settings — branding for cert PDFs + renewal policy */}
        <AdminWorkspaceSettings />

        {/* Client-side observability log */}
        <AdminClientLogs />

        {/* Danger zone: workspace cleanup */}
        <AdminDangerZone />
      </div>

      <Dialog open={!!editing} onOpenChange={() => setEditing(null)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader><DialogTitle className="font-display">Roles for {editing?.name}</DialogTitle></DialogHeader>
          <div className="space-y-2">
            {roles.map((r) => (
              <label key={r} className="flex items-center gap-3 p-2 hover:bg-slate-50 rounded">
                <Checkbox data-testid={`admin-role-${r.replace(/\s/g, "-")}`} checked={selected.includes(r)} onCheckedChange={() => toggle(r)} />
                <span className="text-sm font-medium">{r}</span>
              </label>
            ))}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditing(null)}>Cancel</Button>
            <Button onClick={save} data-testid="admin-save-roles" className="bg-slate-900 hover:bg-[#0055FF] text-white">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Create inspection token */}
      <Dialog open={tokenDialogOpen} onOpenChange={setTokenDialogOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-display">New Inspection Link</DialogTitle>
            <DialogDescription>
              Mint a time-boxed, read-only link for an auditor. Every access is logged; revoke anytime.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div><Label className="text-xs font-semibold">Label / purpose *</Label>
              <Input data-testid="admin-insp-label" value={tokenForm.label}
                     onChange={(e) => setTokenForm({ ...tokenForm, label: e.target.value })}
                     className="mt-1.5" placeholder="e.g. FDA 510(k) inspection Q3 2026" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs font-semibold">Inspector name</Label>
                <Input value={tokenForm.inspector_name}
                       onChange={(e) => setTokenForm({ ...tokenForm, inspector_name: e.target.value })}
                       className="mt-1.5" /></div>
              <div><Label className="text-xs font-semibold">Organization</Label>
                <Input value={tokenForm.inspector_organization}
                       onChange={(e) => setTokenForm({ ...tokenForm, inspector_organization: e.target.value })}
                       className="mt-1.5" placeholder="FDA / BSI / TÜV" /></div>
            </div>
            <div><Label className="text-xs font-semibold">Expires in (hours)</Label>
              <Input type="number" min={1} max={720} value={tokenForm.expires_in_hours} data-testid="admin-insp-hours"
                     onChange={(e) => setTokenForm({ ...tokenForm, expires_in_hours: parseInt(e.target.value) || 72 })}
                     className="mt-1.5" />
              <p className="text-[10px] font-mono text-slate-500 mt-1">Max 720 hours (30 days).</p></div>
            <div>
              <Label className="text-xs font-semibold">Scope — what the auditor can view</Label>
              <div className="mt-1.5 grid grid-cols-2 gap-1 border border-slate-200 rounded p-2">
                {[
                  { v: "device", l: "Device projects" },
                  { v: "capa", l: "CAPA" },
                  { v: "deviation", l: "Deviations" },
                  { v: "change", l: "Change Control" },
                  { v: "document", l: "Documents" },
                  { v: "training_assignment", l: "Training records" },
                  { v: "risk", l: "Risk / FMEA" },
                  { v: "codesign_session", l: "Co-design sessions" },
                ].map((x) => (
                  <label key={x.v} className="flex items-center gap-2 text-sm p-1">
                    <Checkbox data-testid={`admin-insp-scope-${x.v}`}
                              checked={tokenForm.scope_types.includes(x.v)}
                              onCheckedChange={() => toggleScope(x.v)} />
                    {x.l}
                  </label>
                ))}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setTokenDialogOpen(false)}>Cancel</Button>
            <Button onClick={createToken} data-testid="admin-insp-submit"
                    className="bg-slate-900 hover:bg-[#0055FF] text-white">Create link</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Fresh token — show once with copy CTA */}
      <Dialog open={!!freshToken} onOpenChange={() => setFreshToken(null)}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader><DialogTitle className="font-display">Inspection link ready</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <p className="text-sm text-slate-600">
              Share this URL with your inspector. Every access is logged; revoke anytime from the table.
            </p>
            <div className="p-3 bg-slate-900 text-white rounded font-mono text-xs break-all" data-testid="admin-insp-fresh-url">
              {window.location.origin}/inspect/{freshToken?.token}
            </div>
            <Button onClick={() => copyLink(freshToken.token)} className="w-full bg-slate-900 hover:bg-[#0055FF] text-white font-semibold">
              <Copy className="w-4 h-4 mr-2" /> Copy link
            </Button>
            <div className="text-[10px] font-mono text-slate-500 text-center">
              Expires {new Date(freshToken?.expires_at || Date.now()).toLocaleString()}
            </div>
          </div>
        </DialogContent>
      </Dialog>

    </DashboardLayout>
  );
}
